import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import {
  getGlobalConfig,
  putGlobalConfig,
  type WStudioGlobalConfig,
  type WStudioGlobalKey,
} from '@/api/wsr/wstudioGlobalConfig';
import { wsrApiError } from '@/api/wsr/errors';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AppSwitch, HelpPopover } from '@/components/ui';
import { HELP_SCOPE_BY_KEY, HELP_SCOPE_FALLBACK } from '@/domain/globalConfigHelp';
import { RETIRED_KEYS } from '@/domain/retiredGlobalConfigKeys';
import { useCopy } from '@/hooks/useCopy';

type GroupId = 'userList' | 'sales' | 'report' | 'other';
type GlobalTab = 'enable' | 'userList' | 'sales' | 'attendance' | 'other';

function tabForGroup(group: GroupId): Exclude<GlobalTab, 'enable'> {
  return group === 'report' ? 'attendance' : group;
}

interface FieldMeta {
  group: Exclude<GroupId, 'other'>;
  label: string;
  help: string;
  path: boolean;
}

const FIELD_META: Readonly<Record<string, FieldMeta>> = {
  'WStudio.user.listWithFilters': {
    group: 'userList',
    label: 'User list path',
    help: 'Path after the tenant domain. A fixed query string is allowed.',
    path: true,
  },
  'WStudio.user.listWithFilters.max': {
    group: 'userList',
    label: 'User list maximum',
    help: 'Maximum users requested per page.',
    path: false,
  },
  'WStudio.user.listWithFilters.offset': {
    group: 'userList',
    label: 'User list offset',
    help: 'Starting offset for user-list requests.',
    path: false,
  },
  'WStudio.user.listWithFilters.filter': {
    group: 'userList',
    label: 'User list filter',
    help: 'Optional filter sent with user-list requests.',
    path: false,
  },
  'WStudio.sales.listSales': {
    group: 'sales',
    label: 'Tenant sales path',
    help: 'Path after the tenant domain for tenant-wide sales.',
    path: true,
  },
  'WStudio.sales.listForUser': {
    group: 'sales',
    label: 'Per-user sales path',
    help: 'Path after the tenant domain for per-user sales.',
    path: true,
  },
  'WStudio.sales.listForUser.max': {
    group: 'sales',
    label: 'Per-user sales maximum',
    help: 'Maximum sales requested per user.',
    path: false,
  },
  'WStudio.sales.listForUser.offset': {
    group: 'sales',
    label: 'Per-user sales offset',
    help: 'Starting offset for per-user sales requests.',
    path: false,
  },
  'WStudio.sales.listForUser.forceUpdate': {
    group: 'sales',
    label: 'Force update',
    help: 'Value sent as the per-user sales force-update parameter.',
    path: false,
  },
  'WStudio.report.checkin': {
    group: 'report',
    label: 'Attendance report path',
    help: 'Path after the tenant domain for the attendance report.',
    path: true,
  },
};

const FIELD_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  'WStudio.user.listWithFilters.filter': 'Search term',
  'WStudio.user.listWithFilters.filters': 'Roster criteria',
};

const GROUPS: ReadonlyArray<{ id: GroupId; title: string; help: string }> = [
  {
    id: 'userList',
    title: 'User list',
    help: 'Endpoint path and request parameters for the WStudio user list.',
  },
  {
    id: 'sales',
    title: 'Sales',
    help: 'Endpoint paths and request parameters for tenant and per-user sales.',
  },
  {
    id: 'report',
    title: 'Report',
    help: 'Endpoint path for the attendance report.',
  },
  {
    id: 'other',
    title: 'Additional settings',
    help: 'Additional global keys returned by wsr-api.',
  },
];

function groupFor(key: string): GroupId {
  if (key === 'WStudio.user.listWithFilters.filters') return 'userList';
  return FIELD_META[key]?.group ?? 'other';
}

function valuesFrom(keys: WStudioGlobalKey[]): Record<string, string> {
  return Object.fromEntries(keys.map(({ key, value }) => [key, value]));
}

function errorInput(
  field: string | undefined,
  sentEntries: WStudioGlobalKey[],
  keys: WStudioGlobalKey[],
): string | null {
  if (!field) return null;
  if (keys.some((entry) => entry.key === field)) return field;
  const match = /^entries(?:\.|\[)(\d+)\]?(?:\.value)?$/.exec(field);
  if (!match) return null;
  const index = Number(match[1]);
  return sentEntries[index]?.key ?? null;
}

export function GlobalConfig() {
  const { t } = useCopy();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [keys, setKeys] = useState<WStudioGlobalKey[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [initialValues, setInitialValues] = useState<Record<string, string>>({});
  const [active, setActive] = useState(false);
  const [initialActive, setInitialActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errorField, setErrorField] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<GlobalTab>('enable');

  const hydrate = useCallback((config: WStudioGlobalConfig) => {
    const liveKeys = config.keys.filter(({ key }) => !RETIRED_KEYS.has(key));
    const nextValues = valuesFrom(liveKeys);
    setKeys(liveKeys);
    setValues(nextValues);
    setInitialValues(nextValues);
    setActive(config.active);
    setInitialActive(config.active);
    setErrorField(null);
    setErrorMessage(null);
  }, []);

  const reload = () => {
    setLoading(true);
    setLoadError(false);
    getGlobalConfig()
      .then(hydrate)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    getGlobalConfig()
      .then((config) => {
        if (!cancelled) hydrate(config);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hydrate]);

  const markChanged = () => {
    setSaved(false);
    setErrorField(null);
    setErrorMessage(null);
  };

  const changedEntries = keys
    .filter(({ key }) => values[key] !== initialValues[key])
    .map(({ key }) => ({ key, value: values[key] ?? '' }));
  const dirty = active !== initialActive || changedEntries.length > 0;
  const groupTabs = GROUPS.filter((group) =>
    keys.some(({ key }) => groupFor(key) === group.id),
  ).map((group) => tabForGroup(group.id));
  const visibleTabs: GlobalTab[] = ['enable', ...groupTabs];
  const tab = visibleTabs.includes(selectedTab) ? selectedTab : 'enable';

  const tabLabel = (key: GlobalTab): string => {
    if (key === 'enable') return t('globalConfig.tabs.enable', 'Enable');
    if (key === 'attendance') return t('globalConfig.tabs.attendance', 'Attendance');
    if (key === 'other') return t('globalConfig.tabs.other', 'Additional');
    return t(`globalConfig.groups.${key}.title`, key === 'userList' ? 'User list' : 'Sales');
  };

  const handleSave = async () => {
    const invalidPath = changedEntries.find(
      ({ key, value }) => FIELD_META[key]?.path && !value.startsWith('/'),
    );
    if (invalidPath) {
      setErrorField(invalidPath.key);
      setErrorMessage(
        t(
          'globalConfig.validation.path',
          'Enter a path beginning with /. A fixed query is allowed.',
        ),
      );
      setSelectedTab(tabForGroup(groupFor(invalidPath.key)));
      return;
    }

    setSaving(true);
    setSaved(false);
    setErrorField(null);
    setErrorMessage(null);
    try {
      const updated = await putGlobalConfig({ active, entries: changedEntries });
      hydrate(updated);
      setSaved(true);
    } catch (error) {
      const apiError = wsrApiError(error);
      const namedField = errorInput(apiError?.field, changedEntries, keys);
      setErrorField(namedField);
      if (namedField) setSelectedTab(tabForGroup(groupFor(namedField)));
      setErrorMessage(
        apiError?.message ??
          t('globalConfig.saveError', 'Could not save the global WStudio configuration.'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h1" component="h1" gutterBottom>
        {t('page.globalConfig.title', 'Global WStudio config')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          'page.globalConfig.subtitle',
          'Shared endpoint paths and parameters used by every tenant.',
        )}
      </Typography>

      {loading ? <LoadingSpinner /> : null}
      {!loading && loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={reload}>
              {t('common.retry', 'Retry')}
            </Button>
          }
        >
          {t('globalConfig.loadError', 'Could not load the global WStudio configuration.')}
        </Alert>
      ) : null}

      {!loading && !loadError ? (
        <Box
          component="form"
          aria-label={t('globalConfig.formAriaLabel', 'Global WStudio configuration form')}
          onSubmit={(event) => {
            event.preventDefault();
            void handleSave();
          }}
        >
          <Stack spacing={2.5}>
            {errorMessage && !errorField ? <Alert severity="error">{errorMessage}</Alert> : null}
            {saved ? (
              <Alert severity="success">
                {t('globalConfig.saveSuccess', 'Global WStudio configuration saved.')}
              </Alert>
            ) : null}

            <Alert severity="warning">
              {t(
                'globalConfig.notes.silentFailure',
                'Endpoint values are sent to WStudio as written. A wrong value usually returns no data rather than an error — verify with the dry-run tests after any change.',
              )}
            </Alert>
            <Alert severity="info">
              {t(
                'globalConfig.notes.invalidatesVerification',
                "Saving a change bumps the config generation, marking every tenant's dry-run results stale. Affected tenants stop dispatching until their dry runs are re-run.",
              )}
            </Alert>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tab}
                onChange={(_event, value: GlobalTab) => setSelectedTab(value)}
                aria-label={t(
                  'globalConfig.tabs.ariaLabel',
                  'Global WStudio configuration sections',
                )}
                variant="scrollable"
                scrollButtons="auto"
              >
                {visibleTabs.map((key) => (
                  <Tab
                    key={key}
                    value={key}
                    label={tabLabel(key)}
                    id={`global-tab-${key}`}
                    aria-controls={`global-panel-${key}`}
                  />
                ))}
              </Tabs>
            </Box>

            <Box
              id="global-panel-enable"
              role="tabpanel"
              hidden={tab !== 'enable'}
              aria-labelledby="global-tab-enable"
            >
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <AppSwitch
                  label={t('globalConfig.active.label', 'WStudio enabled')}
                  checked={active}
                  onChange={(checked) => {
                    markChanged();
                    setActive(checked);
                  }}
                  disabled={saving}
                />
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'globalConfig.active.help',
                    'Controls whether the shared WStudio configuration is active.',
                  )}
                </Typography>
              </Paper>
            </Box>

            {GROUPS.map((group) => {
              const groupKeys = keys.filter(({ key }) => groupFor(key) === group.id);
              if (groupKeys.length === 0) return null;
              const groupTab = tabForGroup(group.id);
              return (
                <Box
                  key={group.id}
                  id={`global-panel-${groupTab}`}
                  role="tabpanel"
                  hidden={tab !== groupTab}
                  aria-labelledby={`global-tab-${groupTab}`}
                >
                  <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t(`globalConfig.groups.${group.id}.help`, group.help)}
                    </Typography>
                    <Stack spacing={2}>
                      {groupKeys.map(({ key }) => {
                        const meta = FIELD_META[key];
                        const hasError = errorField === key;
                        const copyKey = key.replaceAll('.', '_');
                        const label = t(
                          `globalConfig.fields.${copyKey}.label`,
                          FIELD_LABEL_OVERRIDES[key] ?? meta?.label ?? key,
                        );
                        const detail = t(`globalConfig.fields.${copyKey}.detail`, '');
                        const helpScope = HELP_SCOPE_BY_KEY[key];
                        const fieldId = `global-config-${key.replaceAll('.', '-')}`;
                        return (
                          <Box key={key}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{ alignItems: 'center', mb: 0.5 }}
                            >
                              <Typography component="label" htmlFor={fieldId} variant="body2">
                                {label}
                              </Typography>
                              {detail && helpScope ? (
                                <HelpPopover
                                  label={label}
                                  scope={t(`help.scope.${helpScope}`, HELP_SCOPE_FALLBACK[helpScope])}
                                  body={detail}
                                />
                              ) : null}
                            </Stack>
                            {FIELD_LABEL_OVERRIDES[key] ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 0.5 }}
                              >
                                {key}
                              </Typography>
                            ) : null}
                            <TextField
                              id={fieldId}
                              helperText={
                                hasError
                                  ? errorMessage
                                  : meta
                                    ? t(`globalConfig.fields.${copyKey}.help`, meta.help)
                                    : t(
                                        'globalConfig.fields.unknownHelp',
                                        'Global value supplied by wsr-api.',
                                      )
                              }
                              value={values[key] ?? ''}
                              error={hasError}
                              onChange={(event) => {
                                markChanged();
                                setValues((current) => ({ ...current, [key]: event.target.value }));
                              }}
                              disabled={saving}
                              size="small"
                              fullWidth
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                  </Paper>
                </Box>
              );
            })}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" disabled={!dirty || saving}>
                {saving
                  ? t('globalConfig.saving', 'Saving…')
                  : t('globalConfig.saveAll', 'Save all settings')}
              </Button>
            </Box>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}
